import React, { useRef, useState, useEffect } from 'react';
import { WidgetProps } from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { ic_delete } from 'react-icons-kit/md/ic_delete';
import {
  buttonClasses,
  inputClasses,
} from 'components/common/tailwindClassnames';
import ModalShell from 'components/common/ModalShell';

const CustomFileField: React.FC<WidgetProps> = ({
  onChange,
  value,
  uiSchema,
  label,
  schema,
  id,
}) => {
  const { t } = useTranslation();
  // Helper text can come from a `ui:description` override or the field's schema
  // description — prefer the override, matching how SchemaForm renders descriptions.
  const description =
    (uiSchema?.['ui:description'] as React.ReactNode) ?? schema?.description;
  const [files, setFiles] = useState<File[]>([]);
  const [fileData, setFileData] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null);
  const [editFields, setEditFields] = useState<{ [key: string]: string }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const acceptRaw = uiSchema?.['ui:options']?.accept;
  const accept = Array.isArray(acceptRaw) ? acceptRaw.join(',') : '.xml,.keyx';

  // Initialize component with existing form data (only on mount)
  useEffect(() => {
    if (value && typeof value === 'string') {
      try {
        const parsedValue = JSON.parse(value);
        if (Array.isArray(parsedValue)) {
          setFileData(parsedValue);
        }
      } catch (error) {
        console.error('Failed to parse existing form data:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    if (files.length > 0) {
      handleUpload(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
      ? Array.from(event.target.files)
      : [];

    if (selectedFiles.length === 0 && fileData.length === 0) {
      setFiles([]);
      setErrors([]);
      setFileData([]);
      onChange(null);
    } else {
      setFiles(selectedFiles);
      setErrors([]);
    }
    event.target.value = '';
  };

  const handleUpload = async (selectedFiles = files) => {
    setErrors([]);

    const newFileData: any[] = [];
    const newErrors: string[] = [];

    // Get existing collarIDs
    const loadedCollarIDs = new Set(
      fileData.map((f) => f.parsedData?.collarID),
    );
    const batchCollarIDs = new Set<string>();

    // Get names of already loaded files
    const loadedFileNames = new Set(fileData.map((f) => f.name));

    // Filter out files that are already loaded
    const filesToProcess = selectedFiles.filter(
      (file) => !loadedFileNames.has(file.name),
    );
    const duplicateCollarIDs: string[] = [];

    if (filesToProcess.length === 0) {
      setErrors([t('All selected files are already loaded.')]);
      return;
    }

    const processFile = (file: File): Promise<void> => {
      return new Promise((resolve) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        const isXml =
          file.type === 'text/xml' || ext === 'xml' || ext === 'keyx';
        if (!isXml) {
          newErrors.push(
            t('{{fileName}} is not an XML file.', { fileName: file.name }),
          );
          resolve();
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(content, 'application/xml');

            // Check for parsing errors
            const parseError = xmlDoc.getElementsByTagName('parsererror');
            if (parseError.length > 0) {
              throw new Error(t('Invalid XML format'));
            }

            // Extract required fields
            const collar = xmlDoc.getElementsByTagName('collar')[0];
            const collarID = collar?.getAttribute('ID');
            const collarType =
              collar?.getElementsByTagName('collarType')[0]?.textContent;
            const comIDElement = collar?.getElementsByTagName('comID')[0];
            const comType = comIDElement?.getAttribute('comType');
            const comID = comIDElement?.textContent;
            const key = collar?.getElementsByTagName('key')[0]?.textContent;

            // Validate extracted fields
            if (!collarID || !collarType || !comType || !comID || !key) {
              throw new Error(t('Missing required fields in XML'));
            }

            const parsedData = {
              collarID,
              collarType,
              comType,
              comID,
              key,
            };

            if (loadedCollarIDs.has(collarID) || batchCollarIDs.has(collarID)) {
              duplicateCollarIDs.push(collarID);
            } else {
              batchCollarIDs.add(collarID);
              // Add valid file data
              newFileData.push({
                name: file.name,
                parsedData,
                uploadDate: new Date().toLocaleString(),
              });
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : JSON.stringify(error);
            newErrors.push(
              t('{{fileName}} is not a valid XML: {{error}}', {
                fileName: file.name,
                error: errorMessage,
              }),
            );
          } finally {
            resolve();
          }
        };
        reader.readAsText(file);
      });
    };

    await Promise.all(filesToProcess.map((file) => processFile(file)));

    if (duplicateCollarIDs.length > 0) {
      newErrors.push(
        t('Collars {{collars}} info is already loaded.', {
          collars: [...new Set(duplicateCollarIDs)].join(', '),
        }),
      );
    }

    setErrors((prev) => [
      ...prev,
      ...newErrors.map((error) =>
        typeof error === 'string' ? error : JSON.stringify(error),
      ),
    ]);

    if (newFileData.length === 0) {
      // No new files to add
      return;
    }

    const updated = [...fileData, ...newFileData];
    setFileData(updated);
    onChange(
      JSON.stringify(
        updated.map((file) => ({
          name: file.name,
          parsedData: file.parsedData,
          uploadDate: file.uploadDate,
        })),
      ),
    );
  };

  const handleDelete = (index: number) => {
    const updatedFileData = [...fileData];
    updatedFileData.splice(index, 1);
    setFileData(updatedFileData);

    onChange(
      JSON.stringify(
        updatedFileData.map((file) => ({
          name: file.name,
          parsedData: file.parsedData,
          uploadDate: file.uploadDate,
        })),
      ),
    );
  };

  const handleOpenFileDialog = () => {
    setFiles([]);
    setErrors([]);
    fileInputRef.current?.click();
  };

  const handleFieldChange = (field: string, value: string) => {
    setEditFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = (index: number) => {
    setCurrentEditIndex(index);
    setEditFields({ ...fileData[index].parsedData });
    setIsModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (currentEditIndex !== null) {
      try {
        const updatedFileData = [...fileData];
        updatedFileData[currentEditIndex].parsedData = { ...editFields };
        setFileData(updatedFileData);
        setIsModalOpen(false);

        // Call onChange with updated data
        onChange(
          JSON.stringify(
            updatedFileData.map((file) => ({
              name: file.name,
              parsedData: file.parsedData,
              uploadDate: file.uploadDate,
            })),
          ),
        );
      } catch (error) {
        alert(t('Invalid data. Please correct it before saving.'));
      }
    }
  };

  return (
    <div className="my-4">
      {/* Header: label + description on the left, action button on the right.
          Responsive (Vectronic): below 780px the button drops below the label, left-aligned,
          sitting just above the table; above 780px it returns to the top-right.
          NOTE: 780px is a viewport breakpoint; the real constraint is the card width. If this
          ever renders in a narrow column, switch these toggles to CSS container queries. */}
      <div className="mb-4 flex flex-col items-start gap-4 min-[780px]:flex-row min-[780px]:justify-between">
        <div className="flex flex-col">
          {/* `label` is the ui:title node from generateUISchema — already includes the
              required (*) marker, so render it as-is (don't append another asterisk). */}
          {label && <div className="text-ui-text-primary-text">{label}</div>}
          {description && (
            <span className="text-sm text-ui-text-primary-text">
              {description}
            </span>
          )}
        </div>
        <button
          type="button"
          className={`${buttonClasses.headerActionGreen} flex-shrink-0 whitespace-nowrap`}
          onClick={handleOpenFileDialog}
        >
          {t('Select Collar Registration Files')}
        </button>
        <input
          type="file"
          accept={accept}
          multiple
          onChange={handleFileSelect}
          className="hidden"
          ref={fileInputRef}
          id="file-input"
        />
      </div>

      {/* Display selected filenames */}
      {files.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-bold">{t('Selected Files:')}</h4>
          <ul className="list-disc pl-5">
            {files.map((file, index) => (
              <li key={index} className="text-sm text-ui-text-primary-text">
                {file.name}
              </li>
            ))}
          </ul>
        </div>
      )}
      {errors.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-bold">{t('Errors:')}</h4>
          <ul className="list-disc pl-5">
            {errors.map((error, index) => (
              <li key={index} className="text-sm text-ui-text-primary-text">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Responsive (Vectronic): below 780px the Filename and Upload Date columns are
          hidden (so the table fits a narrow card with just Collar ID + Actions); the
          wide min-width and horizontal scroll only kick in above 780px with all columns.
          NOTE for reviewers: hiding those two columns on small screens is an accepted,
          intentional tradeoff (Collar ID is the key identifier) — not an oversight. */}
      {fileData.length > 0 && (
        <div className="w-full overflow-x-auto rounded-md border border-unlisted-gray-lines">
          <table className="h-auto w-full table-auto cursor-pointer bg-white text-ui-text-primary-text min-[780px]:min-w-[640px]">
            <thead className="border-b border-unlisted-gray-lines bg-teal-t100 px-3 pb-2 pt-3 text-left text-sm font-bold uppercase text-white">
              <tr>
                {/* One column absorbs the slack (w-full) so the remaining columns size to
                    content and the Actions column hugs the right edge. Above 780px that's
                    Filename (the name column); below 780px Filename is hidden, so Collar ID
                    takes over as the absorber. */}
                <th className="w-full whitespace-nowrap border-b border-unlisted-gray-lines bg-teal-t100 p-3 min-[780px]:w-auto">
                  {t('Collar ID')}
                </th>
                <th className="hidden whitespace-nowrap border-b border-unlisted-gray-lines bg-teal-t100 p-3 min-[780px]:table-cell min-[780px]:w-full">
                  {t('Filename')}
                </th>
                <th className="hidden whitespace-nowrap border-b border-unlisted-gray-lines bg-teal-t100 p-3 min-[780px]:table-cell">
                  {t('Upload Date')}
                </th>
                <th className="whitespace-nowrap border-b border-unlisted-gray-lines bg-teal-t100 p-3 text-right">
                  {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {fileData.map((file, index) => (
                <tr
                  key={index}
                  className="border-t border-unlisted-gray-lines bg-white hover:bg-greyscale-11"
                >
                  <td className="w-full whitespace-nowrap p-3 min-[780px]:w-auto">
                    {file.parsedData.collarID}
                  </td>
                  <td className="hidden whitespace-nowrap p-3 min-[780px]:table-cell min-[780px]:w-full">
                    {file.name}
                  </td>
                  <td className="hidden whitespace-nowrap p-3 min-[780px]:table-cell">
                    {file.uploadDate}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className={buttonClasses.headerActionGreen}
                        onClick={() => handleEdit(index)}
                      >
                        {t('Edit')}
                      </button>
                      <button
                        className="p-1 text-unlisted-orange transition-colors hover:text-ui-status-error-destructive"
                        type="button"
                        aria-label={t('Remove')}
                        onClick={() => handleDelete(index)}
                      >
                        <Icon icon={ic_delete} size={24} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ModalShell portals out of the schema form, so its buttons can never
          submit it. */}
      {isModalOpen && (
        <ModalShell
          title={t('Edit Record')}
          onClose={() => setIsModalOpen(false)}
          footer={
            <>
              <button
                type="button"
                className={buttonClasses.primaryAction}
                onClick={handleSaveEdit}
              >
                {t('Save')}
              </button>
              <button
                type="button"
                className={buttonClasses.cancelAction}
                onClick={() => setIsModalOpen(false)}
              >
                {t('Cancel')}
              </button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            {/* Render a text field for each property */}
            {['collarID', 'collarType', 'comType', 'comID', 'key'].map(
              (field) => (
                <div key={field} className="flex w-full flex-col">
                  <label
                    htmlFor={`${id}-edit-${field}`}
                    className="mb-1 text-ui-text-primary-text transition-colors duration-300"
                  >
                    {field}
                  </label>
                  {field === 'key' ? (
                    <textarea
                      id={`${id}-edit-${field}`}
                      className={inputClasses.standard}
                      value={editFields[field] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      rows={4}
                    />
                  ) : (
                    <input
                      id={`${id}-edit-${field}`}
                      className={inputClasses.standard}
                      value={editFields[field] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                    />
                  )}
                </div>
              ),
            )}
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default CustomFileField;
